import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { pretendardFont } from '../../styles/typography';
import lalafanGiantLogo from '../../assets/images/lalafan_logo.svg';

const StyledFooter = styled.footer`
  ${pretendardFont}
  background: #111111;
  color: #ffffff;
  overflow: hidden;
  padding: 60px;
  display: flex;
  flex-direction: column;
  gap: 60px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    padding: 40px 20px;
    gap: 100px;
  }
`;

const TopSection = styled.div`
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 48px;
  width: 100%;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: column;
    gap: 24px;
  }
`;

const LeftColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
  min-width: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 20px;
  }
`;

const CompanyName = styled.p`
  color: #fff;
  font-family: Pretendard;
  font-size: 30px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.75px;
  margin-bottom: 28px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 16px;
    letter-spacing: -0.4px;
    margin-bottom: 20px;
  }
`;

const FooterNav = styled.nav<{ $layout?: 'mobile' | 'desktop' }>`
  display: flex;
  gap: 40px;
  align-items: flex-start;

  ${({ $layout }) =>
    $layout === 'mobile'
      ? `
    display: none;
  `
      : `
    flex-direction: column;
    align-items: flex-end;
    gap: 12px;
    flex-shrink: 0;
  `}

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    ${({ $layout }) =>
      $layout === 'mobile'
        ? `
      display: flex;
      flex-direction: row;
      gap: 24px;
    `
        : `
      display: none;
    `}
  }
`;

const FooterNavList = styled.ul`
  display: flex;
  flex-direction: row;
  gap: 40px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 20px;
  }
`;

const FooterNavLink = styled(Link)<{ $active?: boolean }>`
  color: ${({ $active, theme }) => ($active ? theme.colors.lime : '#ffffff')};
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 25.2px */
  text-decoration: none;
  letter-spacing: -0.45px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 14px;
    font-weight: 700;
    line-height: 140%; /* 19.6px */
    letter-spacing: -0.35px;
  }

  &:hover {
    color: ${({ theme }) => theme.colors.lime};
  }
`;

const DetailsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 28px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    gap: 36px;
    margin-top: 16px;
  }
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoRow = styled.div`
  display: flex;
  gap: 8px;
`;

const InfoLabel = styled.span`
  color: #ffffff;
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  letter-spacing: -0.45px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 14px;
    letter-spacing: -0.35px;
  }
`;

const InfoValue = styled.span`
  color: #fff;
  font-family: Pretendard;
  font-size: 18px;
  font-style: normal;
  font-weight: 700;
  line-height: normal;
  letter-spacing: -0.45px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 14px;
    letter-spacing: -0.35px;
  }
`;

const ContactRow = styled.div`
  display: flex;
  flex-direction: row;
  gap: 8px;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 8px 20px;
  }
`;

const Copyright = styled.p`
  font-family: Pretendard;
  font-size: 15px;
  font-style: normal;
  font-weight: 500;
  line-height: normal;
  letter-spacing: -0.375px;
  color: #ffffff;

  @media (max-width: ${({ theme }) => theme.breakpoints.mobile}) {
    font-size: 13px;
  }
`;

const GiantLogoLink = styled(Link)`
  display: block;
  width: 100%;
  overflow: hidden;
  text-decoration: none;
  cursor: pointer;

  img {
    width: 100%;
    height: auto;
    display: block;
    filter: brightness(0) saturate(100%) invert(88%) sepia(45%) saturate(600%)
      hue-rotate(22deg) brightness(103%) contrast(92%);
  }
`;

const scrollToSection = (id: string) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: 'smooth' });
};

const FooterNavLinks: React.FC<{
  isHome: boolean;
  isContents: boolean;
  isCampaigns: boolean;
  onHomeClick: (event: React.MouseEvent) => void;
}> = ({ isHome, isContents, isCampaigns, onHomeClick }) => (
  <FooterNavList>
    <FooterNavLink to="/" $active={isHome} onClick={onHomeClick}>
      HOME
    </FooterNavLink>
    <FooterNavLink to="/contents" $active={isContents}>
      CONTENTS
    </FooterNavLink>
    <FooterNavLink to="/campaigns" $active={isCampaigns}>
      CAMPAIGNS
    </FooterNavLink>
  </FooterNavList>
);

const Footer: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const isHome = location.pathname === '/';
  const isContents = location.pathname === '/contents';
  const isCampaigns = location.pathname === '/campaigns';

  const handleHomeClick = (event: React.MouseEvent) => {
    if (isHome) {
      event.preventDefault();
      scrollToSection('hero');
    } else {
      navigate('/');
    }
  };

  const navProps = {
    isHome,
    isContents,
    isCampaigns,
    onHomeClick: handleHomeClick,
  };

  return (
    <StyledFooter>
      <TopSection>
        <LeftColumn>
          <CompanyName>(주)빅브라운</CompanyName>
          <FooterNav $layout="mobile" aria-label="푸터 메뉴">
            <FooterNavLinks {...navProps} />
          </FooterNav>
          <DetailsSection>
            <InfoList>
              <InfoRow>
                <InfoLabel>Address</InfoLabel>
                <InfoValue>
                  서울시 강남구 테헤란로 437, 10F(삼성동, 삼영빌딩)
                </InfoValue>
              </InfoRow>
              <ContactRow>
                <InfoRow>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>hello@lalafan.io</InfoValue>
                </InfoRow>
                <InfoRow>
                  <InfoLabel>CEO</InfoLabel>
                  <InfoValue>김용환</InfoValue>
                </InfoRow>
              </ContactRow>
            </InfoList>
            <Copyright>Ⓒ BIIGBROWN. All rights reserved.</Copyright>
          </DetailsSection>
        </LeftColumn>

        <FooterNav $layout="desktop" aria-label="푸터 메뉴">
          <FooterNavLinks {...navProps} />
        </FooterNav>
      </TopSection>

      <GiantLogoLink
        to="/"
        onClick={handleHomeClick}
        aria-label="LALAFAN 홈으로 이동"
      >
        <img src={lalafanGiantLogo} alt="LALAFAN" />
      </GiantLogoLink>
    </StyledFooter>
  );
};

export default Footer;
